import {
  type Observable,
  combineLatest,
  from,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { actionCreator } from '@ardoq/rxbeach';
import { isArdoqError, isEqual } from '@ardoq/common-helpers';
import { transformationsApi } from '@ardoq/api';
import type { ArdoqId } from '@ardoq/api-types';
import { prototypeInterface } from 'modelInterface/prototype/prototypeInterface';
import type {
  ComponentSearchData,
  ComponentSearchViewState,
  SelectedComponent,
} from 'viewpointBuilder/selectContextTab/types';
import { createComponentSearch$ } from 'viewpointBuilder/selectContextTab/componentSearch$';
import { getComponentsSearchCommands } from 'viewpointBuilder/selectContextTab/commands';
import { getHandleTraversalSearchRoutine } from 'viewpointBuilder/selectContextTab/componentSearchRoutines';
import { activeFutureStateIds$ } from '../futureStatePage$';
import { actionNamespaceFutureStateSelectComponents } from '@ardoq/global-consts';

export const futureStateComponentSearch$ = createComponentSearch$(
  actionNamespaceFutureStateSelectComponents
);

export const futureStateComponentsSearchCommands = getComponentsSearchCommands(
  actionNamespaceFutureStateSelectComponents
);

export const futureStateSearchRoutine = getHandleTraversalSearchRoutine(
  futureStateComponentSearch$,
  actionNamespaceFutureStateSelectComponents
);

/** Dispatched when the user applies a component selection from the drawer. */
export const futureStateComponentsApplied = actionCreator<string[]>(
  '[futureState] components applied'
);

export type FutureStateComponentRemovedFromSelectionPayload = {
  componentId: ArdoqId;
};

/** Dispatched to drop a single scope-only component from the persisted selection. */
export const futureStateComponentRemovedFromSelection =
  actionCreator<FutureStateComponentRemovedFromSelectionPayload>(
    '[futureState] component removed from selection'
  );

export type FutureStateSelectionChangedPayload = { futureStateId: ArdoqId };

export const futureStateSelectionChanged =
  actionCreator<FutureStateSelectionChangedPayload>(
    '[futureState] selection changed'
  );

export const futureStateSelectionSeeded = actionCreator<boolean>(
  '[futureState] selection drawer seeded'
);

const toSelectedComponent = (
  component: ComponentSearchData
): SelectedComponent => ({ ...component, isSelected: true });

/**
 * The components in the active future state's persisted selection, resolved
 * from the backend by id. Emitted once to pre-check the drawer.
 */
export const selectedComponents$: Observable<SelectedComponent[]> =
  activeFutureStateIds$.pipe(
    take(1),
    switchMap(ids => transformationsApi.fetchFutureStateSelection(ids)),
    switchMap((result): Observable<SelectedComponent[]> => {
      if (isArdoqError(result)) return of([]);
      const componentIds = result.components.map(component => component._id);
      if (componentIds.length === 0) return of([]);
      return from(
        prototypeInterface.loadComponentsByIdFromAdvancedSearch(componentIds)
      ).pipe(map(({ results }) => results.map(toSelectedComponent)));
    })
  );

export const toEffectiveComponentIds = (
  state: ComponentSearchViewState
): string[] =>
  state.allComponentsSelected
    ? state.loadedComponents.map(({ id }) => id)
    : state.selectedComponentIds;

const toSortedIds = (ids: string[]): string[] => [...ids].sort();

export const futureStateHasSelectionChanged$: Observable<boolean> =
  combineLatest([selectedComponents$, futureStateComponentSearch$]).pipe(
    map(
      ([persistedSelection, state]) =>
        !isEqual(
          toSortedIds(toEffectiveComponentIds(state)),
          toSortedIds(persistedSelection.map(({ id }) => id))
        )
    )
  );
